// frontend/packages/business-console/src/pages/InboxConversation.jsx
//
// Single conversation view. Layout adapts to viewport:
//   - Mobile (<md): full-bleed, sticky header at the top, sticky reply
//     composer pinned to the bottom of the viewport.
//   - Desktop (>=md): centered max-width column, composer renders inline.
//
// Real-time updates: while the page is mounted, an SSE subscription to
// /api/v2/inbox/conversations/:id/events streams `message_appended`
// events. New messages append directly to local state; archived
// conversations reopen if an inbound arrives. The initial fetch on
// mount stays as the source of truth for everything else — SSE just
// avoids polling for the message thread.
//
// Outbound sends are rendered optimistically: handleSend appends the
// new message to local state as soon as the POST resolves, instead of
// waiting for the SSE round-trip or a full refresh(). The SSE event
// for the same _id is deduplicated by handleMessageAppended.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import useMobileDetect from '../hooks/useMobileDetect';
import {
  getConversation,
  promoteToCrm,
  sendReply,
  updateConversation,
} from '../services/inboxService';
import { subscribeToConversationEvents } from '../services/inboxStream';
import MessageThread from '../components/inbox/MessageThread';
import FormSnapshotCard from '../components/inbox/FormSnapshotCard';
import ReplyComposer from '../components/inbox/ReplyComposer';
import {
  INTENT_LABEL,
  URGENCY_META,
  INTENT_CHIP,
} from '../components/inbox/triageMeta';
import AddToCrmButton from '../components/inbox/AddToCrmButton';
import { getInitials } from '@upryzze/ui';

export default function InboxConversation({
  conversationId: propId,
  embedded = false,
} = {}) {
  const params = useParams();
  const id = propId || params.id;
  const navigate = useNavigate();
  const { isMobile } = useMobileDetect();
  // In the desktop split view the thread renders inside a pane (no back
  // button, composer inline, no mobile fixed-bottom). On the standalone
  // route it keeps the full-screen mobile behavior.
  const fixedComposer = isMobile && !embedded;
  const { business } = useContext(BusinessContext);
  const { businessId: activeBusinessId, ensureSignedIn, getToken } = useAuth();

  const inboxPath = useMemo(
    () => (business?.slug ? `/business/${business.slug}/inbox` : '/'),
    [business?.slug]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [snack, setSnack] = useState('');

  const refresh = useCallback(async () => {
    setError('');
    try {
      const result = await getConversation(id);
      setData(result);
      // Mark read on first view (and on refreshes — cheap PATCH).
      if (result?.conversation?._id) {
        try {
          await updateConversation(result.conversation._id, { markRead: true });
        } catch {
          // Non-fatal; the badge will catch up on next summary fetch.
        }
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load conversation'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  // Real-time updates. Subscribe after the initial fetch resolves (we
  // need conversation._id for the subscription URL). Appends new
  // messages to local state and bumps conversation.lastMessageAt /
  // status as the server-side conversation snapshot drifts.
  //
  // Dedup by message._id so the optimistic path (sendReply returning
  // the new message before the SSE event fires) doesn't end up showing
  // duplicates when the SSE event arrives.
  const handleMessageAppended = useCallback(
    (event) => {
      const incoming = event?.message;
      if (!incoming || !incoming._id) return;

      setData((prev) => {
        if (!prev || !prev.conversation) return prev;
        const messages = Array.isArray(prev.messages) ? prev.messages : [];
        const alreadyHave = messages.some(
          (m) => String(m._id) === String(incoming._id)
        );
        if (alreadyHave) return prev;
        return {
          ...prev,
          messages: [...messages, incoming],
          conversation: {
            ...prev.conversation,
            status: event?.conversation?.status ?? prev.conversation.status,
            lastMessageAt:
              event?.conversation?.lastMessageAt ??
              prev.conversation.lastMessageAt,
          },
        };
      });

      // If an inbound message arrived while we're viewing the
      // conversation, mark it read in the background so the nav badge
      // catches up. Failure is non-fatal — next summary fetch will
      // correct it.
      if (incoming.direction === 'inbound' && id) {
        updateConversation(id, { markRead: true }).catch(() => {});
      }
    },
    [id]
  );

  useEffect(() => {
    const conversationId = data?.conversation?._id;
    if (!conversationId) return undefined;
    if (!activeBusinessId) return undefined;

    const unsubscribe = subscribeToConversationEvents(conversationId, {
      ensureSignedIn,
      getToken,
      businessId: activeBusinessId,
      onMessageAppended: handleMessageAppended,
      onError: (err) => {
        // Connection drops are expected (deploys, network blips, etc.).
        // The stream client reconnects automatically; we just log so
        // they're observable in devtools without alarming Sentry.
        // eslint-disable-next-line no-console
        console.warn('[inbox-sse] connection error:', err?.message || err);
      },
    });

    return unsubscribe;
  }, [
    activeBusinessId,
    data?.conversation?._id,
    ensureSignedIn,
    getToken,
    handleMessageAppended,
  ]);

  const conversation = data?.conversation || null;
  const messages = data?.messages || [];
  const submission = data?.submission || null;
  const customer = data?.customer || null;

  // Channel detection mirrors backend formsInboxController.detectReplyChannel:
  // SMS when the source is the Twilio inbound webhook OR the contact has a
  // phone with no email. Composer + labels switch accordingly.
  const conversationChannel =
    conversation?.source?.type === 'smsInbound' ||
    (conversation?.customer?.phone && !conversation?.customer?.email)
      ? 'sms'
      : 'email';

  const customerEmail = conversation?.customer?.email || customer?.email || '';
  const customerPhone = conversation?.customer?.phone || customer?.phone || '';

  // Fallback display name. For cold SMS inbounds with no Customer match,
  // "Website Visitor" is misleading -- they didn't come from the website.
  // Use "SMS contact" so the operator immediately understands the source.
  const customerName =
    conversation?.customer?.name ||
    [customer?.firstName, customer?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    conversation?.customer?.email ||
    (conversationChannel === 'sms' ? 'SMS contact' : 'Website Visitor');

  // Explicit STOP gate. communicationPreferences.sms.optOutAt is set by
  // the Twilio webhook STOP handler. If present, replies must be blocked
  // -- the customer must START before we'll send again.
  const customerSmsOptedOut = Boolean(
    customer?.communicationPreferences?.sms?.optOutAt
  );

  // Optimistic send: append the returned outbound message to local
  // state immediately so the composer-to-thread feedback is
  // SMS-instant. The SSE message_appended event for the same _id is
  // deduped by handleMessageAppended; if the SSE channel is down, the
  // append still stands.
  const handleSend = useCallback(
    async ({ bodyText }) => {
      const newMessage = await sendReply(conversation._id, { bodyText });
      if (!newMessage || !newMessage._id) return;
      setData((prev) => {
        if (!prev || !prev.conversation) return prev;
        const existing = Array.isArray(prev.messages) ? prev.messages : [];
        if (existing.some((m) => String(m._id) === String(newMessage._id))) {
          return prev;
        }
        return {
          ...prev,
          messages: [...existing, newMessage],
          conversation: {
            ...prev.conversation,
            lastMessageAt: newMessage.createdAt || new Date().toISOString(),
            status:
              prev.conversation.status === 'archived'
                ? 'open'
                : prev.conversation.status,
          },
        };
      });
    },
    [conversation]
  );

  const handlePromote = useCallback(async () => {
    await promoteToCrm(conversation._id);
    await refresh();
  }, [conversation, refresh]);

  const handleArchiveToggle = useCallback(async () => {
    if (!conversation?._id) return;
    const next = conversation.status === 'archived' ? 'open' : 'archived';
    try {
      await updateConversation(conversation._id, { status: next });
      setSnack(next === 'archived' ? 'Archived' : 'Reopened');
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Update failed');
    }
  }, [conversation, refresh]);

  if (loading && !data) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !conversation) {
    return (
      <Box sx={{ py: 4 }}>
        <Button
          onClick={() => navigate(inboxPath)}
          startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
          sx={{ textTransform: 'none', mb: 2 }}
        >
          Inbox
        </Button>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!conversation) return null;

  return (
    <Box
      sx={{
        // On the standalone mobile route the composer is pinned to the bottom
        // of the viewport; reserve room for it. In the embedded pane it flows.
        pb: fixedComposer ? '180px' : 0,
        ...(embedded
          ? {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }
          : null),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          mb: 1.5,
          position: embedded ? 'static' : { xs: 'sticky', md: 'static' },
          top: { xs: 56, md: 'auto' },
          zIndex: 1,
          backgroundColor: 'background.default',
          flexShrink: 0,
        }}
      >
        {embedded ? (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'primary.light',
              color: 'primary.main',
              fontSize: 14,
              fontWeight: 800,
            }}
          >
            {getInitials(customerName) || '?'}
          </Box>
        ) : (
          <IconButton
            onClick={() => navigate(inboxPath)}
            aria-label="Back to inbox"
            edge="start"
          >
            <ArrowBackIosNewOutlinedIcon fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {customerName}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexWrap: 'wrap',
              color: 'text.secondary',
              fontSize: 13,
            }}
          >
            {customerEmail && (
              <Box
                component="a"
                href={`mailto:${customerEmail}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 14 }} />
                {customerEmail}
              </Box>
            )}
            {customerPhone && (
              <Box
                component="a"
                href={`tel:${customerPhone}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
                {customerPhone}
              </Box>
            )}
          </Box>
        </Box>

        {!isMobile && (
          <AddToCrmButton
            onPromote={handlePromote}
            alreadyInCrm={Boolean(customer?.inCrm)}
            crmHref={
              customer?._id && business?.slug
                ? `/business/${business.slug}/crm/${customer._id}`
                : ''
            }
          />
        )}
        <Tooltip
          title={conversation.status === 'archived' ? 'Reopen' : 'Archive'}
        >
          <IconButton onClick={handleArchiveToggle}>
            {conversation.status === 'archived' ? (
              <UnarchiveOutlinedIcon />
            ) : (
              <ArchiveOutlinedIcon />
            )}
          </IconButton>
        </Tooltip>
        {isMobile && (
          <>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertOutlinedIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={async () => {
                  setMenuAnchor(null);
                  await handlePromote();
                }}
                disabled={Boolean(customer?.inCrm)}
              >
                {customer?.inCrm ? 'In CRM' : 'Add to CRM'}
              </MenuItem>
              <MenuItem
                onClick={async () => {
                  setMenuAnchor(null);
                  await handleArchiveToggle();
                }}
              >
                {conversation.status === 'archived' ? 'Reopen' : 'Archive'}
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Thread region. In the embedded pane it scrolls inside the pane so the
          composer stays pinned to the pane's bottom; on the route it flows. */}
      <Box
        sx={
          embedded
            ? { flex: 1, minHeight: 0, overflowY: 'auto', px: 0.5 }
            : undefined
        }
      >
        {/* Form snapshot — collapsed by default so the thread is the focus */}
        {(submission || conversation.formSubmissionId) && (
          <Box sx={{ mb: 1.5 }}>
            <FormSnapshotCard
              submission={submission}
              definition={data?.definition}
            />
          </Box>
        )}

        <Divider />

        {/* Thread */}
        <MessageThread messages={messages} />
      </Box>

      {/* AI triage summary (read-only): urgency + intent + suggested next
          action, so the operator sees the recommendation before replying.
          Present only after the background triage sweep classifies it. */}
      {conversation.aiTriage?.intent ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, md: 0 },
            py: 1,
          }}
        >
          {URGENCY_META[conversation.aiTriage.urgency] && (
            <Box
              component="span"
              sx={{
                px: 1,
                py: '2px',
                borderRadius: '10px',
                fontSize: 11.5,
                fontWeight: 700,
                color: URGENCY_META[conversation.aiTriage.urgency].fg,
                bgcolor: URGENCY_META[conversation.aiTriage.urgency].bg,
              }}
            >
              {URGENCY_META[conversation.aiTriage.urgency].label}
            </Box>
          )}
          {INTENT_LABEL[conversation.aiTriage.intent] && (
            <Box
              component="span"
              sx={{
                px: 1,
                py: '2px',
                borderRadius: '10px',
                fontSize: 11.5,
                fontWeight: 700,
                color: INTENT_CHIP.fg,
                bgcolor: INTENT_CHIP.bg,
              }}
            >
              {INTENT_LABEL[conversation.aiTriage.intent]}
            </Box>
          )}
          {conversation.aiTriage.nextAction ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Suggested: {conversation.aiTriage.nextAction}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {/* Composer — pinned to the viewport bottom only on the standalone
          mobile route; inline on desktop and in the embedded pane. */}
      <Box
        sx={{
          position: fixedComposer ? 'fixed' : 'static',
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'background.default',
          borderTop: fixedComposer ? 1 : 0,
          borderColor: 'divider',
          px: fixedComposer ? 2 : { xs: 2, md: 0 },
          py: { xs: 1.5, md: 2 },
          zIndex: 2,
          flexShrink: 0,
        }}
      >
        <ReplyComposer
          onSend={handleSend}
          conversationId={conversation._id}
          channel={conversationChannel}
          customerEmail={customerEmail}
          customerPhone={customerPhone}
          disabledReason={
            conversation.status === 'archived'
              ? 'Reopen this conversation to send a reply.'
              : conversationChannel === 'sms'
                ? !customerPhone
                  ? 'This conversation has no customer phone to reply to.'
                  : customerSmsOptedOut
                    ? 'Customer opted out via STOP. They must reply START before we can text them again.'
                    : ''
                : !customerEmail
                  ? 'This conversation has no customer email to reply to.'
                  : ''
          }
        />
      </Box>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={2500}
        onClose={() => setSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snack}
      />
    </Box>
  );
}
